import { Component, computed, Signal, signal, ViewChild, WritableSignal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router } from '@angular/router';
import { ConfirmationDialogComponent } from '@app/components';
import { RL1Transmitter } from '@app/models/payroll/rl1Transmitter.model';
import { T619 } from '@app/models/payroll/t619.model';
import { TaxReturnSummary } from '@app/models/payroll/tax-return-summary.abstract.model';
import { TaxReturn } from '@app/models/payroll/tax-return.model';
import { TaxFormConsentDialog } from '@app/modules/payroll/components/tax-returns/tax-form-consent-dialog/tax-form-consent-dialog.component';
import { TaxReturnService } from '@app/modules/payroll/services/tax-return.service';
import { AuthService, NotifyService } from '@app/services';
import { PayrollSettingsService, Settings } from '@app/services/payroll-settings.service';
import { T4Summary } from '@models/payroll/t4-summary.model';
import { TranslateService } from '@ngx-translate/core';
import { format } from 'date-fns';
import { from, of } from 'rxjs';
import { catchError, switchMap, take } from 'rxjs/operators';

type ReturnType = 'T4' | 'RL1';

@Component({
    templateUrl: './tax-return-submit.template.html',
    styleUrls: ['./tax-return-submit.style.scss'],
})
export class TaxReturnSubmit {
    @ViewChild('autoSubmitModal') autoSubmitModal!: ConfirmationDialogComponent;
    @ViewChild('manualSubmitModal') manualSubmitModal!: ConfirmationDialogComponent;
    @ViewChild(TaxFormConsentDialog) bulkEmailConfirmationDialog!: TaxFormConsentDialog;
    loading = true;
    /** The checkboxes presented to the user before submitting */
    confirmations: WritableSignal<boolean>[] = [];
    manualSubmission = false;
    t619?: T619;
    returnType: ReturnType = 'T4';
    /** Manual submission of T4s requires adding a CRA confirmation # */
    craConfirmationNumber = signal<string | null>(null);
    /** Manual submission of RL-1s requires checking a box declaring you have submitted to RQ */
    rqManualConfirmation = signal<boolean>(false);
    summary!: TaxReturnSummary;
    settings: Signal<Settings | undefined> = toSignal(
        from(this.settingsService.load()).pipe(
            switchMap(() => this.settingsService.settings$),
            catchError((e) => {
                this.notify.error(e);
                return of(undefined);
            })
        )
    );

    /** The submission card is shown for a submitted return or if the user has agreed to every checkbox */
    isSubmissionCardVisible = computed(() => {
        if (this.summary.isSubmitted) {
            return true;
        }

        return this.confirmations.every((confirmation) => confirmation());
    });
    payrollEnabled = false;

    constructor(
        private route: ActivatedRoute,
        private taxReturnService: TaxReturnService,
        private notify: NotifyService,
        private auth: AuthService,
        private router: Router,
        private settingsService: PayrollSettingsService,
        private translate: TranslateService
    ) {
        // Pulls the return type from the route path, currently paths are setup for "T4" and "RL1"
        this.returnType = (route.parent?.routeConfig?.path as ReturnType) ?? 'T4';

        // Creates the confirmation checkboxes depending on the confirmations defined in the translation assets
        // This allows for easy configuration. Currently T4s have 2 confirmations and RL1s have 3 confirmations
        for (
            let i = 0;
            i < this.translate.instant(`payroll.taxReturns.submit.${this.returnType}.confirmations`).length;
            i += 1
        ) {
            this.confirmations.push(signal(false));
        }
        this.loadSummary();
        this.payrollEnabled = this.auth.company.isPayrollSyncEnabled;
    }

    /**
     * The TaxReturn model resolver will return a T4Summary when the RL-1 flow is disabled via the feature flag.
     * We need to know the difference because the T4Summary is nested on the new TaxReturn model
     */
    isLegacyT4Summary(model: T4Summary | TaxReturn): model is T4Summary {
        return model instanceof T4Summary;
    }

    async downloadTaxReturnPreview(): Promise<void> {
        this.loading = true;
        try {
            await this.taxReturnService.downloadTaxReturnPreview(this.summary);
        } catch (e) {
            this.notify.error(e);
        } finally {
            this.loading = false;
        }
    }

    /**
     * Show CRA/RQ confirmation input if user has issued the summary with the manual submission option
     */
    get isManualSubmissionInProgress(): boolean {
        if (this.returnType === 'T4' && this.t619 === undefined) return false;

        return !!this.summary.manualSelectedAt && this.summary.isSubmitted && !this.hasSubmittedConfirmation;
    }

    /**
     * The final view with the confirmation number and date details
     */
    get shouldShowFinishScreen(): boolean {
        if (!this.summary.submittedAt) return false;

        if (this.summary.manualSelectedAt) {
            return this.hasSubmittedConfirmation;
        }

        return true;
    }

    /**
     * Whether the CRA number has been entered and submitted to the backend, or RL-1 checkbox has been ticked
     */
    get hasSubmittedConfirmation(): boolean {
        if (this.summary.isRL1Summary()) {
            // If the RL-1 has a confirmation number that means it has been auto-submitted
            // If the RL-1 has the confirmed manual submission flag that means the checkbox has been ticked by the admin to confirm forms were sent to RQ
            return !!this.summary.rqConfirmationNumber || !!this.summary.hasConfirmedManualSubmission;
        }
        // If the T4 has a confirmation number that means it has been manual or auto submitted.
        // The equality check here ensures that we are not counting a confirmation # that an admin is still entering in the form
        return !!this.t619?.craConfirmationNumber && this.t619?.craConfirmationNumber === this.craConfirmationNumber();
    }

    /**
     * Either auto-submits to CRA or marks a summary for manual submission
     */
    async onSubmit({ isManualSubmit }: { isManualSubmit: boolean }): Promise<void> {
        const confirmed = isManualSubmit ? await this.manualSubmitModal?.open() : await this.autoSubmitModal?.open();

        if (!confirmed) return;

        this.loading = true;

        this.taxReturnService
            .issue(this.summary, { isManualSubmit })
            .pipe(take(1))
            .subscribe({
                next: (summary) => {
                    this.summary = summary;
                    this.loadSummaryDetails();
                },
                error: (e) => {
                    this.notify.error(e);
                    this.loadSummaryDetails();
                },
            });
    }

    async downloadSelfSubmitDocuments(): Promise<void> {
        this.loading = true;
        try {
            await this.taxReturnService.downloadSelfSubmitDocuments(this.summary);
        } catch (e) {
            this.notify.error(e);
        }
        this.loading = false;
    }

    /**
     * Saves the confirmation number (CRA or RQ) after the user has manually added it
     */
    async submitConfirmation(): Promise<void> {
        try {
            this.loading = true;
            if (this.summary.isT4Summary() && this.t619) {
                this.t619.craConfirmationNumber = this.craConfirmationNumber();
                await this.t619.save();

                // Submission was successful, update status and completedAt
                // This update should be happening on the backend as well, but we don't have
                // access to field values sent back as a result of the save operation
                this.summary.t619State = 'complete';
            } else if (this.summary.isRL1Summary()) {
                // Because all of the properties we need from the RL1Transmitter are returned on the RL1Summary, we don't need to persist a reference to the transmitter
                // We only need it here so that we can send a PUT request to update the hasConfirmedManualSubmission value
                await new RL1Transmitter({
                    id: this.summary.rqFormRl1TransmitterId,
                    hasConfirmedManualSubmission: true,
                })
                    .param('company', this.auth.company.id)
                    .save();
                // Need to manually set these properties manually similar to t619 since these are updated on the BE
                this.summary.hasConfirmedManualSubmission = true;
                this.summary.rl1TransmitterState = 'complete';
            }
            this.summary.completedAt = format(new Date(), 'YYYY-MM-DD');
        } catch (e) {
            this.notify.error(e);
        } finally {
            this.loading = false;
        }
    }

    async downloadTaxPackage(): Promise<void> {
        try {
            this.loading = true;
            await this.taxReturnService.downloadTaxPackage(this.summary);
        } catch (e) {
            this.notify.error(e);
        } finally {
            this.loading = false;
        }
    }

    async sendBulkEmails(): Promise<void> {
        const confirmed = await this.bulkEmailConfirmationDialog?.openDialog();

        if (!confirmed) return;

        try {
            this.loading = true;
            await this.taxReturnService.sendBulkEmails(this.summary);
            this.notify.success(`payroll.taxForms.${this.returnType}.emailDialog.success`);
        } catch (e) {
            this.notify.error(e);
        } finally {
            this.loading = false;
        }
    }

    /**
     * Populates the summary property based on the current route path and model type
     */
    private loadSummary(): void {
        const taxReturn: T4Summary | TaxReturn = this.route.snapshot.data.models.get('taxReturn');
        let summary: TaxReturnSummary | null = null;
        if (this.isLegacyT4Summary(taxReturn)) {
            if (this.returnType === 'RL1') {
                // Legacy summary does not allow for RL1
                this.router.navigate(['../T4'], { relativeTo: this.route });
                return;
            } else {
                // Legacy summary is just a T4Summary without parent TaxReturn
                summary = taxReturn;
            }
        } else {
            if (this.returnType === 'RL1') {
                summary = taxReturn.rl1Summary;
            } else {
                summary = taxReturn.t4Summary;
            }
        }

        // If this page was reached before the summary was generated, send them back
        if (!summary) {
            this.router.navigate(['../../'], { relativeTo: this.route });
            return;
        }

        this.summary = summary;

        this.loadSummaryDetails();
    }

    /**
     * Loads various summary details after the initial summary is loaded
     */
    private loadSummaryDetails(): void {
        if (this.summary.isSubmitted) {
            this.confirmations.forEach((confirmation) => confirmation.set(true));
        }
        if (this.summary.manualSelectedAt) {
            this.manualSubmission = true;
        }

        if (this.summary.isT4Summary()) {
            T619.param('company', this.auth.company.id)
                .find(this.summary.craFormT619Id)
                .then((t619) => {
                    this.t619 = t619;
                    this.craConfirmationNumber.set(t619.craConfirmationNumber);
                    this.loading = false;
                });
        } else if (this.summary.isRL1Summary()) {
            this.rqManualConfirmation.set(this.summary.hasConfirmedManualSubmission);
            this.loading = false;
        }
    }
}
